import axios from "axios";
import * as XLSX from "xlsx";
import { useEffect, useRef, useState } from "react";
import { server } from "../../server";

function CheckPaymentStatus() {
  // ---------- Single lookup state ----------
  const [paymentId, setpaymentId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState("");

  // ---------- Bulk upload state ----------
  const fileInputRef = useRef(null);
  const [bulkRows, setBulkRows] = useState([]); // [{order_id, ...otherFields}]
  const [bulkResults, setBulkResults] = useState([]); // [{order_id, status, message}]
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const [delayMs, setDelayMs] = useState(150);
  const stopRequestedRef = useRef(false);

  const fetchSingleStatus = async (e) => {
    e?.preventDefault();
    if (paymentId.length !== 8) return;

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${server}/get_seesion_api`,
        { order_id: paymentId },
        {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        },
      );

      const rawBody = response.data["body-json"]?.body;
      const body = rawBody ? JSON.parse(rawBody) : {};

      if (body?.message?.includes("FeeDetail successfully verified")) {
        setPaymentDetails(body.feeDetail);
      } else {
        setPaymentDetails(body.message || "Payment failed or not charged.");
      }
    } catch (err) {
      console.error("Error fetching payment status:", err);
      setError("Something went wrong while fetching payment status.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (paymentId.length === 8) {
      fetchSingleStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentId]);

  // ---------- Helpers ----------
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // Try to find an order_id-like value regardless of header casing/spacing
  const extractOrderId = (row) => {
    const key = Object.keys(row).find(
      (k) => k.trim().toLowerCase().replace(/\s|_/g, "") === "orderid",
    );
    if (!key) return null;
    const val = row[key];
    return val === undefined || val === null ? null : String(val).trim();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });

        const rows = json
          .map((row) => ({ order_id: extractOrderId(row), raw: row }))
          .filter((row) => row.order_id);

        setBulkRows(rows);
        setBulkResults([]);
        setBulkProgress({ done: 0, total: rows.length });
      } catch (err) {
        console.error("Error parsing excel file:", err);
        alert("Could not read the file. Please upload a valid Excel/CSV file.");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const triggerFilePicker = () => {
    fileInputRef.current?.click();
  };

  const runBulkCheck = async () => {
    if (!bulkRows.length || bulkRunning) return;

    setBulkRunning(true);
    stopRequestedRef.current = false;
    const results = [];

    for (let i = 0; i < bulkRows.length; i++) {
      if (stopRequestedRef.current) break;

      const { order_id } = bulkRows[i];
      let status = "failed";
      let message = "";

      try {
        const response = await axios.post(
          `${server}/get_seesion_api`,
          { order_id },
          {
            headers: {
              Authorization: `${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          },
        );

        const rawBody = response.data["body-json"]?.body;
        const body = rawBody ? JSON.parse(rawBody) : {};

        if (body?.message?.includes("FeeDetail successfully verified")) {
          status = "success";
          message =
            typeof body.feeDetail === "string"
              ? body.feeDetail
              : JSON.stringify(body.feeDetail);
        } else {
          status = "failed";
          message = body?.message || "Payment failed or not charged.";
        }
      } catch (err) {
        status = "failed";
        message =
          err?.response?.data?.message || err.message || "Request error.";
      }

      results.push({ order_id, status, message });
      setBulkResults([...results]);
      setBulkProgress({ done: i + 1, total: bulkRows.length });

      if (i < bulkRows.length - 1) {
        await sleep(delayMs);
      }
    }

    setBulkRunning(false);
  };

  const stopBulkCheck = () => {
    stopRequestedRef.current = true;
  };

  const resetBulk = () => {
    setBulkRows([]);
    setBulkResults([]);
    setBulkProgress({ done: 0, total: 0 });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const downloadResults = () => {
    if (!bulkResults.length) return;
    const ws = XLSX.utils.json_to_sheet(
      bulkResults.map((r) => ({
        order_id: r.order_id,
        status: r.status,
        message: r.message,
      })),
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Results");
    XLSX.writeFile(wb, "payment_status_results.xlsx");
  };

  const successCount = bulkResults.filter((r) => r.status === "success").length;
  const failedCount = bulkResults.filter((r) => r.status === "failed").length;

  return (
    <div id="db-wrapper">
      <main id="page-content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12 col-md-12 col-12">
              <div className="m-3 mt-2 pt-1 d-flex justify-content-between">
                <div className="d-flex align-items-center mt-2">
                  <span
                    onClick={() => window.history.back()}
                    className="back-button me-3 cursor-pointer"
                  >
                    <i className="bi bi-arrow-left fs-3"></i>
                  </span>
                  <nav aria-label="breadcrumb">
                    <h4 className="m-0 lh-sm">Check Payment Status</h4>
                    <ol className="breadcrumb pe-3 lh-sm">
                      <li className="breadcrumb-item">
                        <a href="#">Fees Collection</a>
                      </li>
                      <li
                        className="breadcrumb-item active"
                        aria-current="page"
                      >
                        Check Payment Status
                      </li>
                    </ol>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ---------------- Single lookup ---------------- */}
        <section className="mx-3 bg-light">
          <div className="container-fluid">
            <div className="col-lg-7 col-md-8 col-12">
              <div className="row align-items-center justify-content-center bg-white p-4 rounded-4">
                <div className="d-flex flex-column">
                  <h1 className="fw-medium mb-0 fs-4 lh-1">
                    Enter Payment ID
                    <br />
                    <span className="mb-0 text-secondary fw-normal fs-5">
                      Payment ID to retrieve the student's details & online
                      payment details.
                    </span>
                  </h1>

                  <div className="col-7 mt-3">
                    <form
                      className="d-flex align-items-center"
                      onSubmit={fetchSingleStatus}
                    >
                      <span className="position-absolute ps-4">
                        <i className="bi bi-person fs-4" />
                      </span>
                      <input
                        type="search"
                        value={paymentId}
                        autoFocus
                        onChange={(e) => setpaymentId(e.target.value)}
                        className="form-control ps-7 fs-5 lg-search-box"
                        maxLength={8}
                        placeholder="Enter Payment ID"
                      />
                    </form>
                  </div>

                  {loading && (
                    <div
                      className="spinner-border spinner-border-sm mt-3"
                      role="status"
                    ></div>
                  )}
                  {error && <p className="text-danger mt-3">{error}</p>}
                  {paymentDetails && (
                    <div className="mt-2">
                      <div className="text-success">
                        <div
                          className="alert alert-warning lh-1 fs-5 rounded-3 mb-0 text-capitalize"
                          role="alert"
                        >
                          {typeof paymentDetails === "string"
                            ? paymentDetails
                            : JSON.stringify(paymentDetails, null, 2)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-5">
                  <h1 className="fw-medium mb-0 fs-4 lh-1">
                    Bulk Check from Excel
                    <br />
                    <span className="mb-0 text-secondary fw-normal fs-5">
                      Upload an Excel/CSV file containing an{" "}
                      <code>order_id</code> column to check status for many
                      records at once.
                    </span>
                  </h1>

                  <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleFileChange}
                      className="d-none"
                    />
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm py-2 rounded-pill px-5"
                      onClick={triggerFilePicker}
                      disabled={bulkRunning}
                    >
                      <i className="bi bi-upload me-2"></i>
                      Choose Excel File
                    </button>

                    {bulkRows.length > 0 && !bulkRunning && (
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={runBulkCheck}
                      >
                        <i className="bi bi-play-fill me-2"></i>
                        Start Checking ({bulkRows.length})
                      </button>
                    )}

                    {bulkRunning && (
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={stopBulkCheck}
                      >
                        <i className="bi bi-stop-fill me-2"></i>
                        Stop
                      </button>
                    )}

                    {bulkRows.length > 0 && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={resetBulk}
                        disabled={bulkRunning}
                      >
                        Reset
                      </button>
                    )}

                    {bulkResults.length > 0 && (
                      <button
                        type="button"
                        className="btn btn-outline-dark"
                        onClick={downloadResults}
                      >
                        <i className="bi bi-download me-2"></i>
                        Download Results
                      </button>
                    )}
                  </div>

                  {bulkRows.length > 0 && (
                    <div className="mt-3 text-secondary">
                      Found <strong>{bulkRows.length}</strong> records with{" "}
                      <code>order_id</code>.
                    </div>
                  )}

                  {bulkProgress.total > 0 && (
                    <div className="mt-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span>
                          Progress: {bulkProgress.done} / {bulkProgress.total}
                        </span>
                        <span>
                          <span className="text-success me-3">
                            Success: {successCount}
                          </span>
                          <span className="text-danger">
                            Failed: {failedCount}
                          </span>
                        </span>
                      </div>
                      <div className="progress" style={{ height: 8 }}>
                        <div
                          className="progress-bar bg-success"
                          role="progressbar"
                          style={{
                            width: `${
                              bulkProgress.total
                                ? (bulkProgress.done / bulkProgress.total) * 100
                                : 0
                            }%`,
                          }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default CheckPaymentStatus;
